/**
 * User Management page (INVI HQ / admin). Lists users scoped to the caller's
 * dealer group, supports the invite (set-password) onboarding flow, dealer-group
 * assignment, deactivation, search, and a read-only Roles directory tab.
 *
 * Tenant scoping and system-tier protection are enforced server-side in
 * routers/users.py — the gating here is UX only, mirroring that backend.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"

import Layout from "../../components/Layout"
import { AuditRooftopOption, listAuditRooftops } from "../../api/audit"
import { API } from "../../config/api"
import { THEME } from "../../config/theme"
import { useAuth, DealerGroupBrief, Role, User } from "../../context/AuthContext"
import { formatRoleName } from "../../utils/roleName"

const TOKEN_KEY = "invi_token"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ApiUser extends User {
  created_at?: string | null
  invite_pending?: boolean
}

type ModalMode = "add" | "edit" | "reset" | null
type TabKey = "users" | "roles"

interface EditingUser {
  id: number | null
  username: string
  email: string
  first_name: string
  last_name: string
  role_ids: number[]
  dealer_group_id: number | null
  // Home store — the store the audit page opens on for this user. A default
  // view, not a lock: they can switch stores on the page. Null = All stores.
  rooftop_id: number | null
  is_active: boolean
}

const EMPTY_USER: EditingUser = {
  id: null,
  username: "",
  email: "",
  first_name: "",
  last_name: "",
  role_ids: [],
  dealer_group_id: null,
  rooftop_id: null,
  is_active: true,
}

// Minimum length the reset-password modal enforces before submitting; the
// backend re-validates full strength.
const MIN_PASSWORD_LENGTH = 8

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem(TOKEN_KEY)
  return token
    ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" }
}

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return "—"
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
}

const fullName = (u: ApiUser): string => {
  const name = [u.first_name, u.last_name].filter(Boolean).join(" ").trim()
  return name || "—"
}

type StatusKind = "active" | "pending" | "off"

// "Pending invite" is driven by the explicit invite_pending flag — set only for
// invite-flow accounts that haven't accepted yet, and cleared on first login /
// set-password. Established users (created before this flag, or who set a
// password directly) are never falsely shown as pending.
const userStatus = (u: ApiUser): { label: string; kind: StatusKind } => {
  if (!u.is_active) return { label: "Deactivated", kind: "off" }
  if (u.invite_pending) return { label: "Pending invite", kind: "pending" }
  return { label: "Active", kind: "active" }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const UserManagementPage: React.FC = () => {
  const { hasPermission, isLoading: authLoading, user: currentUser, refreshUser } = useAuth()
  const navigate = useNavigate()

  const [tab, setTab] = useState<TabKey>("users")
  const [search, setSearch] = useState("")

  const [users, setUsers] = useState<ApiUser[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [dealerGroups, setDealerGroups] = useState<DealerGroupBrief[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [modal, setModal] = useState<ModalMode>(null)
  const [editing, setEditing] = useState<EditingUser>(EMPTY_USER)
  const [modalError, setModalError] = useState<string | null>(null)
  const [modalSubmitting, setModalSubmitting] = useState(false)

  const [resetNewPassword, setResetNewPassword] = useState("")
  const [resetConfirmPassword, setResetConfirmPassword] = useState("")

  // Home-store options for the add/edit modal, scoped to the group the user
  // will belong to. Served by the audit rooftops endpoint, so only stores
  // with an inventory feed are offered — a home store with no feed would
  // open the audit page onto nothing.
  const [rooftopOptions, setRooftopOptions] = useState<AuditRooftopOption[]>([])

  // A platform operator picks any dealer group; everyone else is locked to
  // their own group, so the group dropdown is hidden for them.
  const canChooseGroup = hasPermission("manage_all_dealer_groups")
  const canManageSystemUsers = hasPermission("manage_system_users")

  // Permission gate
  useEffect(() => {
    if (authLoading) return
    if (!hasPermission("manage_users")) {
      navigate("/dashboard", { replace: true })
    }
  }, [authLoading, hasPermission, navigate])

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const requests = [
        fetch(`${API.baseUrl}${API.endpoints.users}`, { headers: authHeaders() }),
        fetch(`${API.baseUrl}${API.endpoints.roles}`, { headers: authHeaders() }),
      ]
      // Only platform operators choose a group, so only they need the list.
      if (canChooseGroup) {
        requests.push(
          fetch(`${API.baseUrl}${API.endpoints.dealerGroups}`, { headers: authHeaders() }),
        )
      }
      const [usersResp, rolesResp, groupsResp] = await Promise.all(requests)
      if (!usersResp.ok) throw new Error("Failed to load users")
      if (!rolesResp.ok) throw new Error("Failed to load roles")
      setUsers(await usersResp.json())
      setRoles(await rolesResp.json())
      if (groupsResp) {
        setDealerGroups(groupsResp.ok ? await groupsResp.json() : [])
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load data")
    } finally {
      setLoading(false)
    }
  }, [canChooseGroup])

  useEffect(() => {
    if (authLoading || !hasPermission("manage_users")) return
    loadData()
  }, [authLoading, hasPermission, loadData])

  // A platform operator's rooftop list follows the group chosen in the modal;
  // a dealership admin sends no param and the server scopes to their group.
  const rooftopScopeGroupId = canChooseGroup ? editing.dealer_group_id : undefined
  useEffect(() => {
    if (modal !== "add" && modal !== "edit") return
    if (canChooseGroup && rooftopScopeGroupId == null) {
      // No group yet — a home store can't exist without one.
      setRooftopOptions([])
      return
    }
    let cancelled = false
    listAuditRooftops(rooftopScopeGroupId)
      .then((response) => {
        if (!cancelled) setRooftopOptions(response.rooftops)
      })
      .catch(() => {
        if (!cancelled) setRooftopOptions([])
      })
    return () => {
      cancelled = true
    }
  }, [modal, canChooseGroup, rooftopScopeGroupId])

  // -------------------------------------------------------------------------
  // System-tier user protection (mirrors routers/users.py; see RBAC.md)
  // -------------------------------------------------------------------------

  const isRoleProtected = useCallback((role: Role | null | undefined): boolean => {
    if (!role) return false
    return role.permissions.some((p) => p.name === "manage_system_users")
  }, [])

  const visibleRoles = useMemo(() => {
    if (canManageSystemUsers) return roles
    return roles.filter((r) => !isRoleProtected(r))
  }, [roles, canManageSystemUsers, isRoleProtected])

  const canModifyUser = useCallback(
    (u: ApiUser): boolean => {
      if (canManageSystemUsers) return true
      return !isRoleProtected(u.role)
    },
    [canManageSystemUsers, isRoleProtected],
  )

  // -------------------------------------------------------------------------
  // Modal handlers
  // -------------------------------------------------------------------------

  const openAddModal = () => {
    setEditing({ ...EMPTY_USER })
    setModalError(null)
    setModal("add")
  }

  const openEditModal = (u: ApiUser) => {
    setEditing({
      id: u.id,
      username: u.username,
      email: u.email ?? "",
      first_name: u.first_name ?? "",
      last_name: u.last_name ?? "",
      role_ids: (u.roles ?? []).map((r) => r.id),
      dealer_group_id: u.dealer_group?.id ?? null,
      rooftop_id: u.rooftop?.id ?? null,
      is_active: u.is_active,
    })
    setModalError(null)
    setModal("edit")
  }

  const openResetModal = (u: ApiUser) => {
    setEditing({ ...EMPTY_USER, id: u.id, username: u.username })
    setResetNewPassword("")
    setResetConfirmPassword("")
    setModalError(null)
    setModal("reset")
  }

  const closeModal = () => {
    setModal(null)
    setEditing(EMPTY_USER)
    setModalError(null)
    setResetNewPassword("")
    setResetConfirmPassword("")
  }

  const submitAdd = async () => {
    setModalError(null)
    if (!editing.username.trim()) {
      setModalError("Username is required")
      return
    }
    if (!editing.email.trim()) {
      setModalError("Email is required — the invite link is sent there")
      return
    }
    setModalSubmitting(true)
    try {
      // No password: the backend mints a set-password token and emails the
      // welcome/invite link. dealer_group_id is only sent by a platform
      // operator; a dealership admin is forced to their own group server-side.
      const body: Record<string, unknown> = {
        username: editing.username.trim(),
        email: editing.email.trim(),
        first_name: editing.first_name.trim() || null,
        last_name: editing.last_name.trim() || null,
        role_ids: editing.role_ids,
      }
      if (canChooseGroup) body.dealer_group_id = editing.dealer_group_id
      body.rooftop_id = editing.rooftop_id
      const resp = await fetch(`${API.baseUrl}${API.endpoints.users}`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(body),
      })
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({ detail: "Create failed" }))
        throw new Error(data.detail || "Create failed")
      }
      await loadData()
      closeModal()
    } catch (e) {
      setModalError(e instanceof Error ? e.message : "Create failed")
    } finally {
      setModalSubmitting(false)
    }
  }

  const submitEdit = async () => {
    if (editing.id === null) return
    setModalError(null)
    setModalSubmitting(true)
    try {
      const body: Record<string, unknown> = {
        username: editing.username.trim(),
        email: editing.email.trim() || null,
        first_name: editing.first_name.trim() || null,
        last_name: editing.last_name.trim() || null,
        role_ids: editing.role_ids,
      }
      if (canChooseGroup) body.dealer_group_id = editing.dealer_group_id
      // Always sent: an explicit null clears the home store (the backend
      // distinguishes absent from null, and absent would mean "unchanged").
      body.rooftop_id = editing.rooftop_id
      const resp = await fetch(`${API.baseUrl}${API.endpoints.users}/${editing.id}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify(body),
      })
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({ detail: "Update failed" }))
        throw new Error(data.detail || "Update failed")
      }
      await loadData()
      // Your own account: the browser's copy of your profile (home store,
      // roles) dates from login, so refresh it or the change only shows after
      // the next login (Ian moved himself to Toyota and saw no change, 2026-10-09).
      if (editing.id === currentUser?.id) await refreshUser()
      closeModal()
    } catch (e) {
      setModalError(e instanceof Error ? e.message : "Update failed")
    } finally {
      setModalSubmitting(false)
    }
  }

  const submitReset = async () => {
    if (editing.id === null) return
    setModalError(null)
    if (resetNewPassword.length < MIN_PASSWORD_LENGTH) {
      setModalError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters`)
      return
    }
    if (resetNewPassword !== resetConfirmPassword) {
      setModalError("Passwords do not match")
      return
    }
    setModalSubmitting(true)
    try {
      const resp = await fetch(
        `${API.baseUrl}${API.endpoints.users}/${editing.id}/reset-password`,
        {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ new_password: resetNewPassword }),
        },
      )
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({ detail: "Reset failed" }))
        throw new Error(data.detail || "Reset failed")
      }
      await loadData()
      closeModal()
    } catch (e) {
      setModalError(e instanceof Error ? e.message : "Reset failed")
    } finally {
      setModalSubmitting(false)
    }
  }

  // -------------------------------------------------------------------------
  // Row actions
  // -------------------------------------------------------------------------

  const [rowBusyId, setRowBusyId] = useState<number | null>(null)

  const resendInvite = async (u: ApiUser) => {
    setRowBusyId(u.id)
    setError(null)
    try {
      const resp = await fetch(`${API.baseUrl}${API.endpoints.resendInvite(u.id)}`, {
        method: "POST",
        headers: authHeaders(),
      })
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({ detail: "Resend failed" }))
        throw new Error(data.detail || "Resend failed")
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Resend failed")
    } finally {
      setRowBusyId(null)
    }
  }

  const setActive = async (u: ApiUser, active: boolean) => {
    setRowBusyId(u.id)
    setError(null)
    try {
      // Deactivate uses the dedicated DELETE (soft-deactivate); reactivate is
      // a plain PATCH flipping is_active back on.
      const resp = active
        ? await fetch(`${API.baseUrl}${API.endpoints.users}/${u.id}`, {
            method: "PATCH",
            headers: authHeaders(),
            body: JSON.stringify({ is_active: true }),
          })
        : await fetch(`${API.baseUrl}${API.endpoints.users}/${u.id}`, {
            method: "DELETE",
            headers: authHeaders(),
          })
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({ detail: "Update failed" }))
        throw new Error(data.detail || "Update failed")
      }
      await loadData()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Update failed")
    } finally {
      setRowBusyId(null)
    }
  }

  // -------------------------------------------------------------------------
  // Derived
  // -------------------------------------------------------------------------

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase()
    const matches = (u: ApiUser) =>
      !q ||
      u.username.toLowerCase().includes(q) ||
      (u.email ?? "").toLowerCase().includes(q) ||
      fullName(u).toLowerCase().includes(q)
    return [...users].filter(matches).sort((a, b) => a.username.localeCompare(b.username))
  }, [users, search])

  // -------------------------------------------------------------------------
  // Styles
  // -------------------------------------------------------------------------

  const inputStyle: React.CSSProperties = {
    width: "100%",
    height: "40px",
    border: `1px solid ${THEME.colors.silver}`,
    borderRadius: THEME.borderRadius.md,
    padding: "0 12px",
    fontSize: "14px",
    fontFamily: THEME.fonts.primary,
    color: THEME.colors.neutral.light.textPrimary,
    boxSizing: "border-box",
    marginBottom: "12px",
    outline: "none",
  }

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "13px",
    fontWeight: 600,
    color: THEME.colors.neutral.light.textPrimary,
    marginBottom: "4px",
  }

  const primaryBtn: React.CSSProperties = {
    padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
    background: THEME.colors.navy,
    color: THEME.colors.white,
    fontWeight: 500,
    fontSize: "14px",
    border: "none",
    borderRadius: THEME.borderRadius.md,
    cursor: "pointer",
    fontFamily: THEME.fonts.primary,
    transition: "opacity 0.15s",
  }

  const secondaryBtn: React.CSSProperties = {
    padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
    background: THEME.colors.neutral.light.surface,
    color: THEME.colors.neutral.light.textPrimary,
    fontWeight: 500,
    fontSize: "14px",
    border: `1px solid ${THEME.colors.silver}`,
    borderRadius: THEME.borderRadius.md,
    cursor: "pointer",
    fontFamily: THEME.fonts.primary,
  }

  const linkBtn: React.CSSProperties = {
    background: "none",
    border: "none",
    color: THEME.colors.navy,
    fontWeight: 500,
    fontSize: "13px",
    cursor: "pointer",
    padding: "0 6px",
    fontFamily: THEME.fonts.primary,
  }

  const dangerLinkBtn: React.CSSProperties = {
    ...linkBtn,
    color: THEME.colors.semantic.danger,
  }

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
    fontSize: "14px",
    fontWeight: active ? 600 : 500,
    cursor: "pointer",
    color: active ? THEME.colors.navy : THEME.colors.neutral.light.textSecondary,
    borderBottom: `2px solid ${active ? THEME.colors.navy : "transparent"}`,
    marginBottom: "-1px",
    background: "none",
    border: "none",
    borderBottomWidth: "2px",
    borderBottomStyle: "solid",
    fontFamily: THEME.fonts.primary,
  })

  // -------------------------------------------------------------------------
  // Group dropdown (shared by add/edit) — platform operators only
  // -------------------------------------------------------------------------

  const renderGroupSelect = () => {
    if (!canChooseGroup) return null
    return (
      <>
        <label style={labelStyle}>Dealer group</label>
        <select
          value={editing.dealer_group_id ?? ""}
          onChange={(e) =>
            setEditing({
              ...editing,
              dealer_group_id: e.target.value ? Number(e.target.value) : null,
              // A group move invalidates the old group's home store.
              rooftop_id: null,
            })
          }
          style={inputStyle}
        >
          <option value="">— No group (platform / INVI staff) —</option>
          {dealerGroups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
      </>
    )
  }

  // -------------------------------------------------------------------------
  // Home-store dropdown (shared by add/edit) — the store the audit page opens
  // on for this user. A default view, not a lock: they can switch stores on
  // the page (Ian, 2026-09-11).
  // -------------------------------------------------------------------------

  const renderRooftopSelect = () => {
    const groupMissing = canChooseGroup && editing.dealer_group_id === null
    return (
      <>
        <label style={labelStyle}>Home store</label>
        <select
          value={editing.rooftop_id ?? ""}
          onChange={(e) =>
            setEditing({
              ...editing,
              rooftop_id: e.target.value ? Number(e.target.value) : null,
            })
          }
          style={inputStyle}
          disabled={groupMissing}
        >
          <option value="">
            {groupMissing
              ? "— Select a dealer group first —"
              : "— No home store (opens on All stores) —"}
          </option>
          {rooftopOptions.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
      </>
    )
  }

  // -------------------------------------------------------------------------
  // Modal render
  // -------------------------------------------------------------------------

  const renderModal = () => {
    if (modal === null) return null

    const titles: Record<Exclude<ModalMode, null>, string> = {
      add: "Add User",
      edit: "Edit User",
      reset: "Reset Password",
    }

    const addCanSubmit =
      editing.username.trim().length > 0 && editing.email.trim().length > 0
    const canSubmit = !modalSubmitting && (modal === "add" ? addCanSubmit : true)

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
        onClick={closeModal}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "460px",
            maxHeight: "90vh",
            overflowY: "auto",
            background: THEME.colors.neutral.light.surface,
            border: `1px solid ${THEME.colors.silver}`,
            borderRadius: THEME.borderRadius.lg,
            padding: "28px",
            boxSizing: "border-box",
            fontFamily: THEME.fonts.primary,
          }}
        >
          <div
            style={{
              fontSize: "20px",
              fontWeight: 500,
              color: THEME.colors.neutral.light.textPrimary,
              marginBottom: "20px",
            }}
          >
            {titles[modal]}
          </div>

          {modal === "reset" ? (
            <>
              <div
                style={{
                  fontSize: "13px",
                  color: THEME.colors.neutral.light.textSecondary,
                  marginBottom: "16px",
                }}
              >
                Setting new password for <strong>{editing.username}</strong>. They
                will be required to change it on next login.
              </div>
              <label style={labelStyle}>New password</label>
              <input
                type="password"
                value={resetNewPassword}
                onChange={(e) => setResetNewPassword(e.target.value)}
                style={inputStyle}
                autoComplete="new-password"
              />
              <label style={labelStyle}>Confirm new password</label>
              <input
                type="password"
                value={resetConfirmPassword}
                onChange={(e) => setResetConfirmPassword(e.target.value)}
                style={inputStyle}
                autoComplete="new-password"
              />
            </>
          ) : (
            <>
              <div style={{ display: "flex", gap: "12px" }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>First name</label>
                  <input
                    type="text"
                    value={editing.first_name}
                    onChange={(e) => setEditing({ ...editing, first_name: e.target.value })}
                    style={inputStyle}
                    autoComplete="off"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Last name</label>
                  <input
                    type="text"
                    value={editing.last_name}
                    onChange={(e) => setEditing({ ...editing, last_name: e.target.value })}
                    style={inputStyle}
                    autoComplete="off"
                  />
                </div>
              </div>

              <label style={labelStyle}>Username</label>
              <input
                type="text"
                value={editing.username}
                onChange={(e) => setEditing({ ...editing, username: e.target.value })}
                style={inputStyle}
                autoComplete="off"
              />

              <label style={labelStyle}>Email</label>
              <input
                type="email"
                value={editing.email}
                onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                style={inputStyle}
                autoComplete="off"
              />

              {renderGroupSelect()}
              {renderRooftopSelect()}

              <label style={labelStyle}>Roles</label>
              <div
                style={{
                  border: `1px solid ${THEME.colors.silver}`,
                  borderRadius: THEME.borderRadius.md,
                  padding: "8px 12px",
                  marginBottom: "12px",
                  maxHeight: "160px",
                  overflowY: "auto",
                }}
              >
                {visibleRoles.length === 0 ? (
                  <div style={{ fontSize: "13px", color: THEME.colors.neutral.light.textSecondary }}>
                    No roles available
                  </div>
                ) : (
                  visibleRoles.map((r) => {
                    const checked = editing.role_ids.includes(r.id)
                    return (
                      <label
                        key={r.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          fontSize: "14px",
                          color: THEME.colors.neutral.light.textPrimary,
                          padding: "4px 0",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) =>
                            setEditing({
                              ...editing,
                              role_ids: e.target.checked
                                ? [...editing.role_ids, r.id]
                                : editing.role_ids.filter((id) => id !== r.id),
                            })
                          }
                          style={{ marginRight: "8px" }}
                        />
                        {formatRoleName(r.name)}
                      </label>
                    )
                  })
                )}
              </div>
              <div style={{ fontSize: "12px", color: THEME.colors.neutral.light.textSecondary, marginTop: "-4px", marginBottom: "12px" }}>
                A user's abilities are everything their selected roles allow, combined.
              </div>

              {modal === "add" && (
                <div
                  style={{
                    fontSize: "13px",
                    color: THEME.colors.neutral.light.textSecondary,
                    background: THEME.colors.neutral.light.background,
                    border: `1px solid ${THEME.colors.silver}`,
                    borderRadius: THEME.borderRadius.md,
                    padding: "10px 12px",
                    marginBottom: "12px",
                  }}
                >
                  No password is set here. The user receives a welcome email with a
                  single-use link to set their own password.
                </div>
              )}
            </>
          )}

          {modalError && (
            <div
              style={{
                background: "rgba(220, 38, 38, 0.08)",
                color: THEME.colors.semantic.danger,
                border: "0.5px solid rgba(220, 38, 38, 0.3)",
                padding: "10px 12px",
                borderRadius: THEME.borderRadius.md,
                marginBottom: "12px",
                fontSize: "13px",
              }}
            >
              {modalError}
            </div>
          )}

          <div
            style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px" }}
          >
            <button type="button" style={secondaryBtn} onClick={closeModal} disabled={modalSubmitting}>
              Cancel
            </button>
            <button
              type="button"
              style={{
                ...primaryBtn,
                opacity: canSubmit ? 1 : 0.5,
                cursor: canSubmit ? "pointer" : "not-allowed",
              }}
              onClick={modal === "add" ? submitAdd : modal === "edit" ? submitEdit : submitReset}
              disabled={!canSubmit}
            >
              {modalSubmitting
                ? "Saving..."
                : modal === "add"
                ? "Send invite"
                : modal === "edit"
                ? "Save changes"
                : "Reset password"}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // -------------------------------------------------------------------------
  // Users tab
  // -------------------------------------------------------------------------

  const statusColor: Record<StatusKind, string> = {
    active: THEME.colors.semantic.success,
    pending: THEME.colors.semantic.warning,
    off: THEME.colors.neutral.light.textSecondary,
  }

  const renderUsersTab = () => (
    <div
      style={{
        background: THEME.colors.neutral.light.surface,
        borderRadius: THEME.borderRadius.md,
        border: `1px solid ${THEME.colors.silver}`,
        overflow: "hidden",
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "14px",
          fontFamily: THEME.fonts.primary,
        }}
      >
        <thead>
          <tr style={{ background: THEME.auditTable.titleBarBackgroundLight }}>
            {["Name", "Dealer group", "Role", "Status", "Last active", "Created", "Actions"].map((h) => (
              <th
                key={h}
                style={{
                  textAlign: "left",
                  padding: "10px 14px",
                  fontWeight: 500,
                  fontSize: "12px",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  color: THEME.colors.neutral.light.textSecondary,
                  borderBottom: `1px solid ${THEME.colors.silver}`,
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={7} style={emptyCellStyle}>
                Loading...
              </td>
            </tr>
          ) : filteredUsers.length === 0 ? (
            <tr>
              <td colSpan={7} style={emptyCellStyle}>
                {search.trim() ? "No users match your search." : "No users found."}
              </td>
            </tr>
          ) : (
            filteredUsers.map((u, idx) => {
              const status = userStatus(u)
              const busy = rowBusyId === u.id
              const isSelf = u.id === currentUser?.id
              return (
                <tr
                  key={u.id}
                  style={{
                    background:
                      idx % 2 === 0
                        ? THEME.colors.neutral.light.surface
                        : THEME.colors.neutral.light.background,
                  }}
                >
                  <td style={cellStyle}>
                    <div style={{ fontWeight: 600 }}>{fullName(u)}</div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: THEME.colors.neutral.light.textSecondary,
                      }}
                    >
                      {u.username}
                      {u.email ? ` · ${u.email}` : ""}
                    </div>
                  </td>
                  <td style={cellStyle}>{u.dealer_group?.name ?? "—"}</td>
                  <td style={cellStyle}>
                    {u.roles && u.roles.length > 0
                      ? u.roles.map((r) => formatRoleName(r.name)).join(", ")
                      : "—"}
                  </td>
                  <td style={cellStyle}>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "3px 10px",
                        borderRadius: THEME.borderRadius.pill,
                        fontSize: "12px",
                        fontWeight: 500,
                        background: `${statusColor[status.kind]}1A`,
                        color: statusColor[status.kind],
                      }}
                    >
                      {status.label}
                    </span>
                  </td>
                  <td style={cellStyle}>{formatDate(u.last_login_at)}</td>
                  <td style={cellStyle}>{formatDate(u.created_at)}</td>
                  <td style={cellStyle}>
                    {canModifyUser(u) ? (
                      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                        <button type="button" style={linkBtn} onClick={() => openEditModal(u)}>
                          Edit
                        </button>
                        {status.kind === "pending" && (
                          <>
                            <span style={{ color: THEME.colors.silver }}>|</span>
                            <button
                              type="button"
                              style={linkBtn}
                              onClick={() => resendInvite(u)}
                              disabled={busy}
                            >
                              Resend invite
                            </button>
                          </>
                        )}
                        <span style={{ color: THEME.colors.silver }}>|</span>
                        <button type="button" style={linkBtn} onClick={() => openResetModal(u)}>
                          Reset password
                        </button>
                        {!isSelf && (
                          <>
                            <span style={{ color: THEME.colors.silver }}>|</span>
                            {u.is_active ? (
                              <button
                                type="button"
                                style={dangerLinkBtn}
                                onClick={() => setActive(u, false)}
                                disabled={busy}
                              >
                                Deactivate
                              </button>
                            ) : (
                              <button
                                type="button"
                                style={linkBtn}
                                onClick={() => setActive(u, true)}
                                disabled={busy}
                              >
                                Reactivate
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    ) : (
                      <span
                        style={{
                          color: THEME.colors.neutral.light.textSecondary,
                          fontSize: "12px",
                          fontStyle: "italic",
                        }}
                        title="Modifying system-tier users requires manage_system_users permission"
                      >
                        Protected
                      </span>
                    )}
                  </td>
                </tr>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )

  // -------------------------------------------------------------------------
  // Roles directory tab
  // -------------------------------------------------------------------------

  const renderRolesTab = () => (
    <div
      style={{
        background: THEME.colors.neutral.light.surface,
        borderRadius: THEME.borderRadius.md,
        border: `1px solid ${THEME.colors.silver}`,
        overflow: "hidden",
      }}
    >
      {roles.map((r, idx) => (
        <div
          key={r.id}
          style={{
            padding: "16px 18px",
            borderBottom:
              idx === roles.length - 1 ? "none" : `1px solid ${THEME.colors.neutral.light.border}`,
          }}
        >
          <div style={{ fontWeight: 600, fontSize: "15px", color: THEME.colors.neutral.light.textPrimary }}>
            {formatRoleName(r.name)}
          </div>
          {r.description && (
            <div
              style={{
                fontSize: "13px",
                color: THEME.colors.neutral.light.textSecondary,
                marginTop: "2px",
              }}
            >
              {r.description}
            </div>
          )}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "10px" }}>
            {r.permissions.length === 0 ? (
              <span style={{ fontSize: "12px", color: THEME.colors.neutral.light.textSecondary }}>
                No permissions
              </span>
            ) : (
              r.permissions.map((p) => (
                <span
                  key={p.id}
                  title={p.description ?? undefined}
                  style={{
                    fontSize: "12px",
                    background: THEME.colors.neutral.light.background,
                    border: `1px solid ${THEME.colors.silver}`,
                    borderRadius: THEME.borderRadius.sm,
                    padding: "3px 8px",
                    color: THEME.colors.neutral.light.textPrimary,
                  }}
                >
                  {p.name}
                </span>
              ))
            )}
          </div>
        </div>
      ))}
    </div>
  )

  // -------------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------------

  if (authLoading || !hasPermission("manage_users")) {
    return (
      <Layout>
        <div />
      </Layout>
    )
  }

  return (
    <Layout>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "18px",
        }}
      >
        <div
          style={{
            fontSize: "24px",
            fontWeight: 500,
            letterSpacing: "-0.5px",
            color: THEME.colors.neutral.light.textPrimary,
          }}
        >
          User Management
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {tab === "users" && (
            <input
              type="text"
              placeholder="Search users…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                height: "38px",
                width: "220px",
                border: `1px solid ${THEME.colors.silver}`,
                borderRadius: THEME.borderRadius.md,
                padding: "0 12px",
                fontSize: "14px",
                fontFamily: THEME.fonts.primary,
                color: THEME.colors.neutral.light.textPrimary,
                outline: "none",
              }}
            />
          )}
          <button type="button" style={primaryBtn} onClick={openAddModal}>
            + Add User
          </button>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: "4px",
          borderBottom: `1px solid ${THEME.colors.silver}`,
          marginBottom: "16px",
        }}
      >
        <button type="button" style={tabStyle(tab === "users")} onClick={() => setTab("users")}>
          Users
        </button>
        <button type="button" style={tabStyle(tab === "roles")} onClick={() => setTab("roles")}>
          Roles directory
        </button>
      </div>

      {error && (
        <div
          style={{
            background: "rgba(220, 38, 38, 0.08)",
            color: THEME.colors.semantic.danger,
            border: "0.5px solid rgba(220, 38, 38, 0.3)",
            padding: "10px 14px",
            borderRadius: THEME.borderRadius.md,
            marginBottom: "16px",
            fontSize: "13px",
          }}
        >
          {error}
        </div>
      )}

      {tab === "users" ? renderUsersTab() : renderRolesTab()}

      {renderModal()}
    </Layout>
  )
}

const cellStyle: React.CSSProperties = {
  padding: "10px 14px",
  borderBottom: `1px solid ${THEME.colors.neutral.light.border}`,
  color: THEME.colors.neutral.light.textPrimary,
  verticalAlign: "middle",
}

const emptyCellStyle: React.CSSProperties = {
  padding: "24px",
  textAlign: "center",
  color: THEME.colors.neutral.light.textSecondary,
}

export default UserManagementPage
